import { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import { useSnackbar } from "notistack";
import {
  Button,
  IconButton,
  Typography,
  InputAdornment,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { useAdminContext } from "../hooks/UseAdminContext";
import { _edit_profile_api } from "../DAL/form/userForm";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../components/headers/DashboardHeader";
import { styled } from "@mui/material/styles";
import { s3baseUri } from "../config/config";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import UserIcon from "../components/assets/images/user.png";
import PhoneInput from "react-phone-number-validation";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const Input = styled("input")({
  display: "none",
});

function EditProfile() {
  const { userInfo, setUserInfo } = useAdminContext();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneData, setPhoneData] = useState({});

  const handleChangePhone = (value, country) => {
    setPhoneData(country);
    setPhoneNumber(value);
  };

  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setProfileImage] = useState(null);
  const [previews, setPreviews] = useState("");

  const [inputs, setInputs] = useState({
    name: "",
    contact_number: "",
    email: "",
    old_image: "",
    profile_image: "",
  });

  const handleChange = (event) => {
    let { value, name } = event.target;

    if (name === "name") {
      value = value.replace(/[^A-Za-z\s]/g, "");
    }

    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", inputs.name);
    formData.append("contact_number", phoneNumber);

    if (file) {
      formData.append("profile_image", inputs.profile_image);
    }

    const result = await _edit_profile_api(formData);
    if (result.code === 200) {
      localStorage.setItem("user", JSON.stringify(result.customer));
      setUserInfo(result.customer);
      enqueueSnackbar(result.message, { variant: "success" });
      setIsLoading(false);
      navigate(`/dashboard`);
    } else {
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const MAX_SIZE_MB = 5;
    const fileSizeInMB = file.size / (1024 * 1024);

    if (fileSizeInMB > MAX_SIZE_MB) {
      enqueueSnackbar(
        "This file is larger than 5MB. Please upload a smaller file.",
        {
          variant: "error",
        }
      );
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar(
        "Only JPG and PNG files are allowed. Please select a valid image file.",
        {
          variant: "error",
        }
      );
      return;
    }

    setProfileImage(file);
    setInputs((prev) => ({
      ...prev,
      ["profile_image"]: file,
    }));

    const preview = URL.createObjectURL(file);
    setPreviews(preview);
  };

  useEffect(() => {
    setInputs((prev) => ({
      ...prev,
      ["name"]: userInfo?.name,
      ["contact_number"]: userInfo?.contact_number,
      ["email"]: userInfo?.user_info?.email,
      ["old_image"]: userInfo?.profile_image ? userInfo?.profile_image : "",
    }));
    setPhoneNumber(userInfo?.contact_number);
  }, [userInfo]);

  useEffect(() => {
    if (!localStorage.getItem("code-verified")) {
      navigate("/login", { replace: true });
    }
  }, []);

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <div className="header-container">
          <DashboardHeader />
        </div>
        <div className="container">
          <div className="text-center mt-3 d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <IconButton onClick={() => navigate("/dashboard")}>
                <ArrowBackIcon sx={{ fontSize: 20, color: "#15978a" }} />
              </IconButton>
            </div>
            <div>
              <h2 className="primary-color mb-2">Edit Profile</h2>
              <p className="mb-0">
                Manage your account information and preferences
              </p>
            </div>
            <div style={{ width: "40px" }}></div>
          </div>

          <div
            className="card shadow-sm border-0"
            style={{
              borderRadius: "20px",
              backgroundColor: "white",
              boxShadow: "0 10px 40px rgba(0,0,0,0.1) !important",
              maxWidth: "900px",
              margin: "0 auto 40px",
            }}
          >
            <div
              className="card-body"
              style={{
                padding: "40px 30px 30px 30px",
              }}
            >
              <form onSubmit={handleUpdate}>
                <div className="row g-4">
                  {/* Profile Picture Section */}
                  <div className="col-12 col-md-4 text-center ">
                    <div className="position-relative d-inline-block">
                      <div
                        style={{
                          width: 180,
                          height: 180,
                          borderRadius: "50%",
                          padding: "8px",
                          border: "2px solid #E2E8F0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          background:
                            "linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)",
                        }}
                      >
                        <Avatar
                          src={
                            previews
                              ? previews
                              : inputs.old_image
                              ? s3baseUri + inputs.old_image
                              : UserIcon
                          }
                          onClick={() =>
                            window.open(
                              previews
                                ? previews
                                : inputs.old_image
                                ? s3baseUri + inputs.old_image
                                : UserIcon,
                              "_blank"
                            )
                          }
                          sx={{
                            width: "100%",
                            height: "100%",
                            border: "4px solid white",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            cursor: "zoom-in",
                          }}
                        />
                        <label htmlFor="icon-button-file">
                          <Input
                            accept="image/*"
                            id="icon-button-file"
                            type="file"
                            name="profile_image"
                            onChange={(e) => handleUpload(e)}
                          />
                          <IconButton
                            component="span"
                            sx={{
                              position: "absolute",
                              bottom: 12,
                              right: 12,
                              backgroundColor: "#15978a",
                              color: "white",
                              boxShadow: "0 4px 10px rgba(21, 151, 138, 0.4)",
                              "&:hover": {
                                backgroundColor: "#0d6b61",
                              },
                              width: 38,
                              height: 38,
                            }}
                          >
                            <PhotoCamera sx={{ fontSize: 20 }} />
                          </IconButton>
                        </label>
                      </div>
                    </div>
                    <Typography
                      variant="caption"
                      className="d-block mt-3"
                      sx={{
                        color: "#64748B",
                        fontWeight: 500,
                      }}
                    >
                      JPG or PNG. Max size 5MB
                    </Typography>
                  </div>

                  {/* Form Fields Section */}
                  <div className="col-12 col-md-8">
                    <div className="d-flex flex-column gap-3">
                      {/* Email Field */}
                      <TextField
                        label="Email Address"
                        variant="outlined"
                        fullWidth
                        required
                        name="email"
                        disabled
                        value={inputs.email}
                        InputLabelProps={{
                          shrink: Boolean(inputs.email),
                        }}
                        sx={{ mt: 1 }}
                      />

                      {/* Name Field */}
                      <TextField
                        label="Full Name"
                        variant="outlined"
                        fullWidth
                        required
                        name="name"
                        value={inputs.name}
                        onChange={handleChange}
                        InputLabelProps={{
                          shrink: Boolean(inputs.name),
                        }}
                        sx={{ mt: 1 }}
                      />

                      {/* Phone Field */}
                      <div
                        className="position-relative"
                        style={{ marginTop: "9px" }}
                      >
                        <div className="small_title">Phone Number *</div>
                        <div>
                          <PhoneInput
                            value={phoneNumber}
                            placeholder=" "
                            setValue={setPhoneNumber}
                            onChange={handleChangePhone}
                            inputClass="phone_input"
                            autoSelectCountry={true}
                            enableSearch={true}
                            countryCodeEditable={false}
                            required={true}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-5">
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Update Profile"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditProfile;
